import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { CAT_DISPLAY } from './cats';
import { formatDelta, formatTime } from './format';
import { computeStats, type RaceRecord } from './history';
import type { HudStore } from './store';

type Tab = 'cats' | 'races';

const when = (ms: number) => new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');

function CatName({ id }: { id: RaceRecord['results'][number]['id'] }) {
  const d = CAT_DISPLAY[id];
  return <><span class="swatch" style={{ background: d.colour }} aria-hidden="true" />{d.short}</>;
}

function CatsTab({ list }: { list: RaceRecord[] }) {
  const s = useMemo(() => computeStats(list), [list]);
  const rows = [...s.cats].sort((a, b) => b.wins - a.wins || (a.avgPlace ?? 9) - (b.avgPlace ?? 9));
  return (
    <>
      <dl class="summary">
        <div><dt>Races</dt><dd>{s.races}</dd></div>
        <div><dt>Fastest win</dt><dd>{s.fastestWin ? <><CatName id={s.fastestWin.id} /> {formatTime(s.fastestWin.seconds)}</> : '—'}</dd></div>
        <div><dt>Photo finishes</dt><dd>{s.photoFinishes}</dd></div>
        <div><dt>Since</dt><dd>{s.first ? when(s.first) : '—'}</dd></div>
      </dl>
      <div class="scroll">
        <table>
          <thead><tr>
            <th scope="col">Cat</th><th scope="col" class="num">Wins</th><th scope="col" class="num">Win %</th><th scope="col" class="num">Top 3</th>
            <th scope="col" class="num">Avg place</th><th scope="col" class="num">Best</th><th scope="col" class="num" title="Ham eaten">🍖</th>
            <th scope="col" class="num" title="Catnip caught">🌀</th><th scope="col" class="num">DSQ</th>
          </tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <th scope="row"><CatName id={c.id} /></th>
                <td class="num">{c.wins}</td>
                <td class="num">{pct(c.wins, c.races)}</td>
                <td class="num">{c.podiums}</td>
                <td class="num">{c.avgPlace === null ? '—' : c.avgPlace.toFixed(2)}</td>
                <td class="num">{c.bestSeconds === null ? '—' : formatTime(c.bestSeconds)}</td>
                <td class="num">{c.hams}</td>
                <td class="num">{c.catnips}</td>
                <td class="num">{c.dsq}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RaceRow({ r, n }: { r: RaceRecord; n: number }) {
  const ranked = [...r.results].sort((a, b) => (a.dsq === b.dsq ? a.place - b.place : a.dsq ? 1 : -1));
  const winner = ranked.find((x) => !x.dsq);
  const notes = [r.joinedMidRace && 'joined mid-race', r.photoFinish && 'photo finish', r.leadChanges && `${r.leadChanges} lead change${r.leadChanges > 1 ? 's' : ''}`].filter(Boolean);
  return (
    <details class="race">
      <summary>
        <span class="no">#{n}</span>
        <span class="at">{when(r.at)}</span>
        <span class="winner">{winner ? <>👑 <CatName id={winner.id} /> {formatTime(winner.seconds)}</> : 'No winner'}</span>
      </summary>
      <ol>
        {ranked.map((x) => (
          <li key={x.id} class={x.dsq ? 'dsq' : ''}>
            <span class="place">{x.dsq ? '—' : x.place}</span><CatName id={x.id} />
            <span class="num">{x.dsq ? 'DSQ' : formatTime(x.seconds)}</span>
          </li>
        ))}
      </ol>
      <p class="notes">
        {r.ham.length > 0 && <span>🍖 {r.ham.map((id) => CAT_DISPLAY[id].short).join(', ')}</span>}
        {r.catnip.map((c, i) => <span key={i}>🌀 {CAT_DISPLAY[c.cat].short}{c.delta !== null && ` ${formatDelta(c.delta)}`}</span>)}
        {notes.length > 0 && <span>{notes.join(' · ')}</span>}
      </p>
    </details>
  );
}

export function HistoryPanel({ store }: { store: HudStore }) {
  const [tab, setTab] = useState<Tab>('cats');
  const [confirming, setConfirming] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = store.historyOpen.value;
  useEffect(() => { if (open) closeRef.current?.focus(); else setConfirming(false); }, [open]);
  if (!open) return null;
  const list = store.history.value;
  const close = () => { store.historyOpen.value = false; };
  const clear = () => {
    if (!confirming) { setConfirming(true); return; }
    store.clearHistory();
    setConfirming(false);
  };
  return (
    <section class="panel history" role="dialog" aria-modal="false" aria-labelledby="history-title">
      <header>
        <h2 id="history-title">Race history</h2>
        <div class="tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'cats'} class={tab === 'cats' ? 'on' : ''} onClick={() => setTab('cats')}>Cats</button>
          <button type="button" role="tab" aria-selected={tab === 'races'} class={tab === 'races' ? 'on' : ''} onClick={() => setTab('races')}>Races ({list.length})</button>
        </div>
        <button type="button" ref={closeRef} class="close" aria-label="Close race history" onClick={close}>✕</button>
      </header>
      {list.length === 0
        ? <p class="empty">No races yet. Every live race watched to the official results is saved here, in this browser. Recorded replays are not counted.</p>
        : tab === 'cats'
          ? <CatsTab list={list} />
          : <div class="scroll races">{[...list].reverse().map((r, i) => <RaceRow key={r.at} r={r} n={list.length - i} />)}</div>}
      <footer>
        <span class="hint">Saved in this browser only.</span>
        {list.length > 0 && <button type="button" class={confirming ? 'danger' : ''} onClick={clear}>{confirming ? 'Really clear all?' : 'Clear history'}</button>}
      </footer>
    </section>
  );
}
